import type { PasswordPolicy } from '@/types/user-settings'

/**
 * 密码允许的字符集：ASCII 可打印字符（0x21 ~ 0x7E），不含空格与中文。
 *
 * 与后端 SecurityPolicyService.PASSWORD_CHARSET（同为 `[!-~]`）逐字对应。字符集不是策略项而是
 * BCrypt 的物理约束：它只消费密钥前 72 字节，一个汉字占 3 字节，按「72 位」放行中文密码等于放行
 * 216 字节并静默截断。限定 ASCII 后 1 字符 == 1 字节，长度上限才是用户可见且成立的那把尺子。
 */
const ASCII_PRINTABLE = /^[!-~]+$/

/**
 * 按后端下发的密码规则逐项判定，违规时返回规则文案，合规返回 null。
 *
 * 规则本体在 security_policy 表，此处只是把 flags 应用到输入上，不写死长度与字符类别——
 * 管理端改策略后前端判定随之变化（此前 DTO 注解与页面 placeholder 各写一套正则，策略配了不生效）。
 * 判定顺序与谓词口径都与后端 validatePassword 对齐：长度 → 字符集 → 四类字符，
 * 谓词只认 ASCII，全角数字「１」这类不再出现「前端拦、后端放」的分叉。
 */
export function findPasswordViolation(password: string, policy: PasswordPolicy): string | null {
  if (password.length < policy.minLength || password.length > policy.maxLength) return policy.ruleText
  if (!ASCII_PRINTABLE.test(password)) return policy.ruleText
  if (policy.requireUppercase && !/[A-Z]/.test(password)) return policy.ruleText
  if (policy.requireLowercase && !/[a-z]/.test(password)) return policy.ruleText
  if (policy.requireDigit && !/[0-9]/.test(password)) return policy.ruleText
  if (policy.requireSpecialChar && !/[^A-Za-z0-9]/.test(password)) return policy.ruleText
  return null
}

/**
 * 校验后端下发的策略形状，不完整即返回 null（视为「未取到」）。
 *
 * 面板本就有「策略没取到就不本地猜规则、复杂度交给后端回错」的降级路径；但若把缺字段的半套策略
 * 当有效值用，`password.length > undefined` 恒为 false，长度上限会静默失效——宁可退回降级路径。
 */
export function readPasswordPolicy(raw: unknown): PasswordPolicy | null {
  if (!raw || typeof raw !== 'object') return null
  const p = raw as Record<string, unknown>
  const complete =
    typeof p.minLength === 'number' &&
    typeof p.maxLength === 'number' &&
    typeof p.ruleText === 'string' &&
    [p.requireUppercase, p.requireLowercase, p.requireDigit, p.requireSpecialChar].every(
      (v) => typeof v === 'boolean',
    )
  return complete ? (p as unknown as PasswordPolicy) : null
}
